import { useCallback } from 'react';
import { useDrop } from 'react-dnd';
import type { ReactNode } from 'react';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { ItemTypes } from '../../../components/DraggableList/utils/item-types.util';
import type { ReorderDragDescriptor } from '../utils/reorder-drawer-state.util';

interface Props {
  readonly children: ReactNode;
  readonly groupIndex: number;
  readonly isEmpty: boolean;
  readonly onDrop: (drag: ReorderDragDescriptor, atIndex: number) => void;
}

// Lets a usage be dropped at the end of an otherwise empty (or shorter)
// group, where no usage card exists yet to carry the drop target itself.
export const ReorderDrawerUsagesDropZone = ({
  children,
  groupIndex,
  isEmpty,
  onDrop,
}: Readonly<Props>) => {
  const [{ isOver }, dropRef] = useDrop<
    ReorderDragDescriptor,
    unknown,
    Readonly<{ isOver: boolean }>
  >(
    () => ({
      accept: ItemTypes.USAGE,
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }) && monitor.canDrop(),
      }),
      drop: (draggedItem: ReorderDragDescriptor, monitor) => {
        if (monitor.didDrop()) {
          return;
        }

        onDrop(draggedItem, Number.MAX_SAFE_INTEGER);
      },
    }),
    [onDrop],
  );

  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      dropRef(node);
    },
    [dropRef],
  );

  return (
    <Stack
      data-group-index={groupIndex}
      ref={setRef}
      spacing={1}
      sx={
        isEmpty
          ? {
              alignItems: 'center',
              bgcolor: isOver ? 'action.hover' : undefined,
              border: 1,
              borderColor: isOver ? 'primary.main' : 'divider',
              borderRadius: 1,
              borderStyle: 'dashed',
              justifyContent: 'center',
              minHeight: 56,
            }
          : undefined
      }
    >
      {isEmpty ? (
        <Typography color="text.secondary" variant="body2">
          Glissez une prestation ici
        </Typography>
      ) : (
        children
      )}
    </Stack>
  );
};
